import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/zeblan/Logo";
import { LogOut } from "lucide-react";

export default function AdminLeads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/leads");
        if (alive) setLeads(data);
      } catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("zeblan_token");
          nav("/admin");
        } else if (alive) {
          setError("Could not load leads.");
        }
      }
    };
    load();
    return () => { alive = false; };
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("zeblan_token");
    localStorage.removeItem("zeblan_admin_email");
    nav("/admin");
  };

  return (
    <main className="min-h-screen bg-cloud" data-testid="admin-leads-page">
      <header className="bg-white border-b border-mist">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Logo variant="light" className="h-14 w-auto" />
          <button
            onClick={logout}
            className="inline-flex items-center gap-2 text-[13px] font-semibold text-navy hover:text-blue transition-colors"
            data-testid="admin-logout"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="text-navy text-[28px] font-extrabold tracking-tightish">Leads</h1>
        <p className="mt-1 text-steel text-[14px]">All submissions, most recent first.</p>

        {error && <div className="mt-6 text-red-600 text-[14px]">{error}</div>}

        {leads === null ? (
          <div className="mt-10 text-steel text-[14px]">Loading…</div>
        ) : leads.length === 0 ? (
          <div className="mt-10 bg-white border border-mist rounded-xl p-10 text-center text-steel">
            No leads yet. Submissions from the site will appear here.
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto bg-white border border-mist rounded-xl">
            <table className="min-w-full text-[14px]">
              <thead>
                <tr className="text-left text-steel text-[12px] uppercase tracking-[0.18em] bg-cloud">
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Size</th>
                  <th className="px-4 py-3">Message</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-mist text-navy align-top" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-steel">{new Date(l.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[11px] font-bold uppercase tracking-[0.18em] bg-mist text-blue rounded-full px-2.5 py-1">
                        {l.type}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">{l.name}</td>
                    <td className="px-4 py-3">{l.email}</td>
                    <td className="px-4 py-3">{l.company || "—"}</td>
                    <td className="px-4 py-3">{l.companySize || "—"}</td>
                    <td className="px-4 py-3 max-w-md whitespace-pre-wrap">{l.message || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
